import React, { useState } from 'react';
import { Shield, Lock, Eye, Server, CheckCircle, AlertTriangle, FileText, Users, Brain, Clock, Database, Key, Activity, Settings, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const DataSecurity = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const securityPillars = [
    {
      icon: Lock,
      title: 'End-to-End Encryption',
      description: 'All data (in transit & at rest) encrypted using industry standards: AES-256, TLS 1.3',
      details: [
        'Protects health records, chats, reports, and AI interactions',
        'Multi-layer encryption for sensitive PHI/PII data',
        'Secure key management with rotation policies',
        'Zero-knowledge architecture for maximum privacy'
      ],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      icon: Shield,
      title: 'HIPAA-Ready, GDPR-Aligned Compliance',
      description: 'Designed to meet global healthcare regulations',
      details: [
        'Strict access controls & audit trails',
        'PHI/PII protected at all layers',
        'Regular compliance assessments',
        'Data processing agreements in place'
      ],
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      icon: Brain,
      title: 'Secure AI & Data Governance',
      description: 'AI systems trained with strict anonymization',
      details: [
        'No personal data used without user consent',
        'Health Genie follows responsible AI guidelines',
        'Data never sold or shared for marketing',
        'Transparent AI decision-making processes'
      ],
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      icon: Server,
      title: 'Multi-Layer Infrastructure Security',
      description: 'Firewalls, intrusion detection, continuous monitoring',
      details: [
        'Encrypted cloud infrastructure',
        'Zero-trust architecture compatible',
        'DDoS protection and rate limiting',
        'Regular security patches and updates'
      ],
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    {
      icon: Key,
      title: 'Secure Access & Identity Management',
      description: 'Multi-Factor Authentication & role-based access control',
      details: [
        'MFA for all user accounts',
        'Role-based permissions',
        'Provider & organization dashboards isolated logically',
        'Session management with automatic timeouts'
      ],
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    {
      icon: Activity,
      title: 'Regular Security Testing',
      description: 'Penetration testing, vulnerability scans, third-party audits',
      details: [
        'Quarterly penetration testing',
        'Continuous vulnerability monitoring',
        'Independent security audits',
        'Proactive patch management'
      ],
      gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
    }
  ];

  const protectionFeatures = [
    { 
      icon: '🔐', 
      title: 'Safe Login + MFA', 
      desc: 'Multi-factor authentication for all accounts',
      color: '#667eea'
    },
    { 
      icon: '💳', 
      title: 'Secure Payments & Billing', 
      desc: 'PCI-DSS compliant payment processing',
      color: '#f5576c'
    },
    { 
      icon: '📄', 
      title: 'Encrypted Medical Documents', 
      desc: 'All medical records secured at rest and in transit',
      color: '#4facfe'
    },
    { 
      icon: '🤖', 
      title: 'AI-Generated Advice Stored Securely', 
      desc: 'Health Genie data protected with encryption',
      color: '#43e97b'
    },
    { 
      icon: '📞', 
      title: 'Secure Teleconsultations', 
      desc: 'End-to-end encrypted video calls',
      color: '#fa709a'
    },
    { 
      icon: '🏥', 
      title: 'Tamper-Proof Surgical & Diagnostic Records', 
      desc: 'Immutable audit logs for all modifications',
      color: '#30cfd0'
    },
    { 
      icon: '👨‍⚕️', 
      title: 'Controlled Access for Providers', 
      desc: 'Granular permissions for healthcare teams',
      color: '#764ba2'
    },
    { 
      icon: '💾', 
      title: 'Continuous Data Backups', 
      desc: 'Automated backup and recovery systems',
      color: '#00f2fe'
    }
  ];

  const certifications = [
    { 
      name: 'HIPAA', 
      status: 'Ready', 
      desc: 'Health Insurance Portability and Accountability Act',
      statusColor: '#43e97b'
    },
    { 
      name: 'GDPR', 
      status: 'Aligned', 
      desc: 'General Data Protection Regulation',
      statusColor: '#4facfe'
    },
    { 
      name: 'ISO 27001', 
      status: 'Pursuing', 
      desc: 'Information Security Management',
      statusColor: '#fa709a'
    },
    { 
      name: 'SOC 2', 
      status: 'Future-Ready', 
      desc: 'Service Organization Control',
      statusColor: '#667eea'
    }
  ];

  const aiPrinciples = [
    {
      title: 'AI Transparency',
      description: 'Clear disclosure when AI is involved in recommendations or decisions',
      icon: Eye
    },
    {
      title: 'No Decision-Making Without Oversight',
      description: 'Human (user/doctor) oversight and approval required for all critical decisions',
      icon: Users
    },
    {
      title: 'Safety Protocols for Health Genie',
      description: 'Operates within defined safety boundaries with continuous monitoring',
      icon: Shield
    },
    {
      title: 'Bias Testing',
      description: 'Regular evaluation and mitigation of algorithmic bias',
      icon: Activity
    },
    {
      title: 'Ethical Algorithms',
      description: 'AI models designed following medical ethics and best practices',
      icon: CheckCircle
    }
  ];

  const faqs = [
    {
      question: 'How is my health data encrypted?',
      answer: 'We use industry-standard AES-256 encryption for data at rest and TLS 1.3 for data in transit. This means your health records, chat messages, and AI interactions are protected with military-grade encryption that would take billions of years to crack.'
    },
    {
      question: 'Who can access my medical records?',
      answer: 'Only you and healthcare providers you explicitly authorize can access your medical records. We implement strict role-based access controls, and every access is logged in our audit trail for your security and compliance.'
    },
    {
      question: 'Is my data shared with third parties?',
      answer: 'No. We never sell or share your personal health data for marketing purposes. Data is only shared with your explicit consent and only with healthcare providers or services you choose to use.'
    },
    {
      question: 'How does GEWO Health handle AI safety?',
      answer: 'Our Health Genie AI operates under strict safety protocols with human oversight. AI recommendations are always reviewed by you or your doctor before any action is taken. We continuously monitor for bias and ensure ethical AI practices.'
    },
    {
      question: 'What happens to my data if I delete my account?',
      answer: 'When you request account deletion, we permanently remove your personal data from our active systems within 30 days, except where required by law to maintain records for healthcare compliance purposes.'
    },
    {
      question: 'How do you prevent data breaches?',
      answer: 'We employ multiple layers of security including firewalls, intrusion detection systems, 24/7 monitoring, regular penetration testing, and continuous vulnerability assessments. Our team is trained to respond rapidly to any security incidents.'
    }
  ];

  return (
    <div style={styles.page}>
      <Navbar />
      
      {/* Hero Section with Custom Illustration */}
      <section style={styles.heroSection}>
        <div style={styles.container}>
          <div style={styles.heroGrid}>
            <div style={styles.heroContent}>
              <div style={styles.heroBadge}>
                <Shield size={20} />
                <span>Enterprise-Grade Security</span>
              </div>
              
              <h1 style={styles.heroTitle}>
                Your Health Data is Safe, Secure & Protected—Always
              </h1>
              
              <p style={styles.heroSubtitle}>
                GEWO Health follows global security standards, encryption practices, and AI safety 
                protocols to keep your information private and secure across every touchpoint.
              </p>
              
              <div style={styles.heroButtons}>
                <button 
                  style={styles.heroPrimaryButton}
                  onClick={() => document.getElementById('security-pillars').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Security Features
                  <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                </button>
                <button style={styles.heroSecondaryButton}>
                  <FileText size={20} style={{ marginRight: '8px' }} />
                  View Privacy Policy
                </button>
              </div>

              {/* Trust Indicators */}
              <div style={styles.trustIndicators}>
                <div style={styles.trustItem}>
                  <CheckCircle size={20} color="#43e97b" />
                  <span>HIPAA-Ready</span>
                </div>
                <div style={styles.trustItem}>
                  <CheckCircle size={20} color="#4facfe" />
                  <span>GDPR-Aligned</span>
                </div>
                <div style={styles.trustItem}>
                  <CheckCircle size={20} color="#fa709a" />
                  <span>256-bit Encryption</span>
                </div>
                <div style={styles.trustItem}>
                  <CheckCircle size={20} color="#667eea" />
                  <span>24/7 Monitoring</span>
                </div>
              </div>
            </div>
            
            {/* Hero Illustration */}
            <div style={styles.heroIllustration}>
              <svg viewBox="0 0 500 500" style={{ width: '100%', height: 'auto' }}>
                <defs>
                  <linearGradient id="shieldGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#667eea', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#764ba2', stopOpacity: 1 }} />
                  </linearGradient>
                  <linearGradient id="dataGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#4facfe', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#00f2fe', stopOpacity: 1 }} />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Background circles */}
                <circle cx="250" cy="250" r="200" fill="#f7f9fc" opacity="0.5"/>
                <circle cx="250" cy="250" r="150" fill="#e3edff" opacity="0.6"/>
                
                {/* Central Shield */}
                <g transform="translate(250, 250)" filter="url(#glow)">
                  <path d="M0 -100 L60 -70 L60 0 C60 50 30 85 0 100 C-30 85 -60 50 -60 0 L-60 -70 Z" 
                        fill="url(#shieldGrad1)" stroke="white" strokeWidth="4"/>
                  
                  {/* Lock inside shield */}
                  <g transform="translate(0, -10)">
                    <rect x="-15" y="5" width="30" height="35" rx="5" fill="white" opacity="0.9"/>
                    <path d="M-12 5 L-12 -10 Q-12 -22 0 -22 Q12 -22 12 -10 L12 5" 
                          stroke="white" strokeWidth="6" fill="none" opacity="0.9"/>
                    <circle cx="0" cy="20" r="6" fill="#667eea"/>
                    <rect x="-2" y="20" width="4" height="12" fill="#667eea"/>
                  </g>
                </g>
                
                {/* Floating Security Icons */}
                {/* Top Left - Database */}
                <g transform="translate(100, 120)">
                  <circle cx="0" cy="0" r="35" fill="url(#dataGrad)" opacity="0.2"/>
                  <circle cx="0" cy="0" r="25" fill="url(#dataGrad)"/>
                  <ellipse cx="0" cy="-8" rx="12" ry="4" fill="white" opacity="0.9"/>
                  <rect x="-12" y="-8" width="24" height="6" fill="white" opacity="0.8"/>
                  <ellipse cx="0" cy="-2" rx="12" ry="4" fill="white" opacity="0.9"/>
                  <ellipse cx="0" cy="5" rx="12" ry="4" fill="white" opacity="0.9"/>
                  <rect x="-12" y="5" width="24" height="6" fill="white" opacity="0.8"/>
                  <ellipse cx="0" cy="11" rx="12" ry="4" fill="white" opacity="0.9"/>
                </g>
                
                {/* Top Right - Key */}
                <g transform="translate(400, 120)">
                  <circle cx="0" cy="0" r="35" fill="#fa709a" opacity="0.2"/>
                  <circle cx="0" cy="0" r="25" fill="#fa709a"/>
                  <circle cx="-8" cy="0" r="8" fill="white" stroke="white" strokeWidth="2"/>
                  <circle cx="-8" cy="0" r="4" fill="#fa709a"/>
                  <rect x="0" y="-2" width="15" height="4" fill="white"/>
                  <rect x="10" y="-6" width="3" height="4" fill="white"/>
                  <rect x="14" y="-6" width="3" height="4" fill="white"/>
                </g>
                
                {/* Bottom Left - Brain/AI */}
                <g transform="translate(120, 380)">
                  <circle cx="0" cy="0" r="35" fill="#43e97b" opacity="0.2"/>
                  <circle cx="0" cy="0" r="25" fill="#43e97b"/>
                  <path d="M-10 -8 Q-12 -12 -8 -14 Q-4 -12 -6 -8 Q-8 -10 -10 -8" fill="white"/>
                  <path d="M2 -8 Q0 -12 4 -14 Q8 -12 6 -8 Q4 -10 2 -8" fill="white"/>
                  <path d="M-8 0 Q-10 -4 -6 -6 Q-2 -4 -4 0" fill="white"/>
                  <path d="M4 0 Q2 -4 6 -6 Q10 -4 8 0" fill="white"/>
                  <path d="M-6 8 Q-8 4 -4 2 Q0 4 -2 8" fill="white"/>
                  <path d="M2 8 Q0 4 4 2 Q8 4 6 8" fill="white"/>
                  <ellipse cx="-2" cy="0" rx="6" ry="10" fill="white" opacity="0.7"/>
                  <ellipse cx="2" cy="0" rx="6" ry="10" fill="white" opacity="0.7"/>
                </g>
                
                {/* Bottom Right - Activity Monitor */}
                <g transform="translate(380, 380)">
                  <circle cx="0" cy="0" r="35" fill="#30cfd0" opacity="0.2"/>
                  <circle cx="0" cy="0" r="25" fill="#30cfd0"/>
                  <polyline points="-12,8 -8,4 -4,10 0,-2 4,6 8,0 12,4" 
                            stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                
                {/* Connection lines */}
                <line x1="100" y1="120" x2="200" y2="180" stroke="#667eea" strokeWidth="2" opacity="0.3" strokeDasharray="5,5"/>
                <line x1="400" y1="120" x2="300" y2="180" stroke="#667eea" strokeWidth="2" opacity="0.3" strokeDasharray="5,5"/>
                <line x1="120" y1="380" x2="200" y2="320" stroke="#667eea" strokeWidth="2" opacity="0.3" strokeDasharray="5,5"/>
                <line x1="380" y1="380" x2="300" y2="320" stroke="#667eea" strokeWidth="2" opacity="0.3" strokeDasharray="5,5"/>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section style={styles.introSection}>
        <div style={styles.container}>
          <div style={styles.introContent}>
            <h2 style={styles.sectionTitle}>Introduction to GEWO Health Security</h2>
            
            <p style={styles.introParagraph}>
              GEWO Health protects <strong>patient</strong>, <strong>provider</strong>, and 
              <strong> organizational data</strong> with enterprise-grade security, using advanced 
              encryption and compliance frameworks.
            </p>
            
            <p style={styles.introParagraph}>
              Every service—<strong>Appointments</strong>, <strong>Surgeries</strong>, 
              <strong> Specializations</strong>, <strong>AI Health Genie</strong>, 
              <strong> Elderly Care</strong>, <strong>Women's Health</strong>, and 
              <strong> Lifestyle Disease Management</strong>—is designed with 
              <span style={{ color: '#667eea', fontWeight: '600' }}> privacy-by-default</span> and 
              <span style={{ color: '#667eea', fontWeight: '600' }}> security-by-design</span> principles.
            </p>
            
            <div style={styles.missionHighlight}>
              <div style={styles.missionIcon}>
                <Shield size={40} color="#667eea" />
              </div>
              <div>
                <h3 style={styles.missionTitle}>Our Mission</h3>
                <p style={styles.missionDesc}>
                  Ensure safe, uninterrupted, and compliant healthcare experiences for every user
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Security Pillars */}
      <section style={styles.pillarsSection} id="security-pillars">
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Core Security Pillars</h2>
          <p style={styles.sectionSubtitle}>
            Six foundational layers protecting your healthcare data
          </p>
          
          <div style={styles.pillarsGrid}>
            {securityPillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <div key={index} style={styles.pillarCard}>
                  <div style={{ ...styles.pillarIconWrapper, background: pillar.gradient }}>
                    <Icon size={32} color="white" />
                  </div>
                  <h3 style={styles.pillarTitle}>{pillar.title}</h3>
                  <p style={styles.pillarDescription}>{pillar.description}</p>
                  <div style={styles.pillarDetailsList}>
                    {pillar.details.map((detail, idx) => (
                      <div key={idx} style={styles.pillarDetailItem}>
                        <div style={styles.checkIcon}>
                          <CheckCircle size={16} color="#43e97b" />
                        </div>
                        <span style={styles.pillarDetailText}>{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How GEWO Health Protects You */}
      <section style={styles.protectionSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>How GEWO Health Protects You</h2>
          <p style={styles.sectionSubtitle}>
            Comprehensive security across every interaction
          </p>
          
          <div style={styles.protectionGrid}>
            {protectionFeatures.map((feature, index) => (
              <div key={index} style={styles.protectionCard}>
                <div style={{ ...styles.protectionIconWrapper, borderColor: feature.color }}>
                  <span style={styles.protectionEmoji}>{feature.icon}</span>
                </div>
                <h3 style={styles.protectionTitle}>{feature.title}</h3>
                <p style={styles.protectionDesc}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Storage & Retention */}
      <section style={styles.dataSection}>
        <div style={styles.container}>
          <div style={styles.dataGrid}>
            <div style={styles.dataIllustration}>
              <svg viewBox="0 0 400 400" style={{ width: '100%', height: 'auto' }}>
                <defs>
                  <linearGradient id="cloudGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#4facfe', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#00f2fe', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                
                {/* Background */}
                <circle cx="200" cy="200" r="150" fill="#f7f9fc"/>
                
                {/* Cloud with lock */}
                <g transform="translate(200, 150)">
                  <ellipse cx="-40" cy="0" rx="40" ry="35" fill="url(#cloudGrad)" opacity="0.9"/>
                  <ellipse cx="0" cy="-15" rx="45" ry="40" fill="url(#cloudGrad)" opacity="0.9"/>
                  <ellipse cx="40" cy="0" rx="40" ry="35" fill="url(#cloudGrad)" opacity="0.9"/>
                  <rect x="-70" y="0" width="140" height="40" fill="url(#cloudGrad)" opacity="0.9"/>
                  
                  {/* Lock */}
                  <g transform="translate(0, 15)">
                    <rect x="-18" y="5" width="36" height="28" rx="4" fill="white"/>
                    <path d="M-12 5 L-12 -8 Q-12 -18 0 -18 Q12 -18 12 -8 L12 5" 
                          stroke="white" strokeWidth="5" fill="none"/>
                    <circle cx="0" cy="18" r="5" fill="#4facfe"/>
                  </g>
                </g>
                
                {/* Database servers below */}
                <g transform="translate(200, 280)">
                  {[-60, 0, 60].map((x, i) => (
                    <g key={i} transform={`translate(${x}, 0)`}>
                      <rect x="-20" y="0" width="40" height="60" rx="4" fill="#667eea" opacity="0.8"/>
                      <ellipse cx="0" cy="0" rx="20" ry="8" fill="#764ba2"/>
                      <ellipse cx="0" cy="0" rx="20" ry="8" fill="#667eea" opacity="0.6"/>
                      <line x1="-15" y1="15" x2="15" y2="15" stroke="#764ba2" strokeWidth="2"/>
                      <line x1="-15" y1="30" x2="15" y2="30" stroke="#764ba2" strokeWidth="2"/>
                      <line x1="-15" y1="45" x2="15" y2="45" stroke="#764ba2" strokeWidth="2"/>
                      <circle cx="0" cy="52" r="3" fill="#43e97b"/>
                    </g>
                  ))}
                </g>
                
                {/* Arrows from cloud to servers */}
                <path d="M200 190 L200 220" stroke="#667eea" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.5"/>
              </svg>
            </div>
            
            <div style={styles.dataContent}>
              <h2 style={styles.dataTitle}>Data Storage & Retention</h2>
              <p style={styles.dataDescription}>
                Your data is stored securely and managed responsibly throughout its lifecycle
              </p>
              
              <div style={styles.dataPoints}>
                <div style={styles.dataPoint}>
                  <div style={styles.dataPointIcon}>
                    <Database size={24} color="#4facfe" />
                  </div>
                  <div>
                    <h4 style={styles.dataPointTitle}>Certified Cloud Datacenters</h4>
                    <p style={styles.dataPointDesc}>
                      Data stored in SOC 2 compliant, enterprise-grade cloud infrastructure
                    </p>
                  </div>
                </div>
                
                <div style={styles.dataPoint}>
                  <div style={styles.dataPointIcon}>
                    <Settings size={24} color="#43e97b" />
                  </div>
                  <div>
                    <h4 style={styles.dataPointTitle}>Automatic Data Minimization</h4>
                    <p style={styles.dataPointDesc}>
                      Only essential information retained—no unnecessary data collection
                    </p>
                  </div>
                </div>
                
                <div style={styles.dataPoint}>
                  <div style={styles.dataPointIcon}>
                    <FileText size={24} color="#fa709a" />
                  </div>
                  <div>
                    <h4 style={styles.dataPointTitle}>Healthcare-Standard Retention</h4>
                    <p style={styles.dataPointDesc}>
                      Retention policies aligned with HIPAA, GDPR, and regional regulations
                    </p>
                  </div>
                </div>
                
                <div style={styles.dataPoint}>
                  <div style={styles.dataPointIcon}>
                    <CheckCircle size={24} color="#667eea" />
                  </div>
                  <div>
                    <h4 style={styles.dataPointTitle}>Safe Deletion on Request</h4>
                    <p style={styles.dataPointDesc}>
                      Complete data removal when requested—your right to be forgotten
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Incident Response & Monitoring */}
      <section style={styles.incidentSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Incident Response & Monitoring</h2>
          <p style={styles.sectionSubtitle}>
            24/7 vigilance to protect your data and respond to threats
          </p>
          
          <div style={styles.incidentGrid}>
            <div style={styles.incidentCard}>
              <div style={styles.incidentIconWrapper}>
                <Clock size={36} color="#4facfe" />
              </div>
              <h3 style={styles.incidentTitle}>24/7 Security Monitoring</h3>
              <p style={styles.incidentDesc}>
                Continuous surveillance of all systems with real-time threat detection and automated alerts
              </p>
            </div>
            
            <div style={styles.incidentCard}>
              <div style={styles.incidentIconWrapper}>
                <AlertTriangle size={36} color="#fa709a" />
              </div>
              <h3 style={styles.incidentTitle}>Real-Time Alerting</h3>
              <p style={styles.incidentDesc}>
                Instant notifications for any suspicious activity or security events requiring attention
              </p>
            </div>
            
            <div style={styles.incidentCard}>
              <div style={styles.incidentIconWrapper}>
                <Activity size={36} color="#43e97b" />
              </div>
              <h3 style={styles.incidentTitle}>Rapid Breach Response Protocol</h3>
              <p style={styles.incidentDesc}>
                Defined escalation path for immediate containment, investigation, and resolution
              </p>
            </div>
            
            <div style={styles.incidentCard}>
              <div style={styles.incidentIconWrapper}>
                <Database size={36} color="#667eea" />
              </div>
              <h3 style={styles.incidentTitle}>Backup & Recovery Mechanisms</h3>
              <p style={styles.incidentDesc}>
                Automated backup systems ensure data availability and business continuity
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Practices */}
      <section style={styles.privacySection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Privacy Practices</h2>
          <p style={styles.sectionSubtitle}>
            Transparent data usage with you in full control
          </p>
          
          <div style={styles.privacyGrid}>
            <div style={styles.privacyCard}>
              <div style={styles.privacyIconCircle}>
                <Eye size={28} color="#4facfe" />
              </div>
              <h3 style={styles.privacyCardTitle}>You Control Your Data</h3>
              <p style={styles.privacyCardDesc}>
                Full control over your health information—decide what's shared and with whom
              </p>
            </div>
            
            <div style={styles.privacyCard}>
              <div style={styles.privacyIconCircle}>
                <FileText size={28} color="#43e97b" />
              </div>
              <h3 style={styles.privacyCardTitle}>Transparent Data Usage</h3>
              <p style={styles.privacyCardDesc}>
                Clear explanation of how data is used—no hidden purposes or fine print
              </p>
            </div>
            
            <div style={styles.privacyCard}>
              <div style={styles.privacyIconCircle}>
                <Shield size={28} color="#fa709a" />
              </div>
              <h3 style={styles.privacyCardTitle}>No Unauthorized Sharing</h3>
              <p style={styles.privacyCardDesc}>
                Your data never sold or shared for marketing—healthcare only
              </p>
            </div>
            
            <div style={styles.privacyCard}>
              <div style={styles.privacyIconCircle}>
                <CheckCircle size={28} color="#667eea" />
              </div>
              <h3 style={styles.privacyCardTitle}>Consent-Based Collection</h3>
              <p style={styles.privacyCardDesc}>
                Permission required for all data usage—explicit consent for every purpose
              </p>
            </div>
          </div>
          
          {/* Rights List - Enhanced Design */}
          <div style={styles.rightsSection}>
            <h3 style={styles.rightsSectionTitle}>Your Data Rights</h3>
            <p style={styles.rightsSectionSubtitle}>
              You have complete control over your personal health information
            </p>
            
            <div style={styles.rightsCardsGrid}>
              <div style={styles.rightCard}>
                <div style={{ ...styles.rightIconWrapper, background: 'linear-gradient(135deg, #43e97b 0%, #38d9a9 100%)' }}>
                  <Eye size={28} color="white" />
                </div>
                <h4 style={styles.rightCardTitle}>Access</h4>
                <p style={styles.rightCardDesc}>
                  View and review all data we hold about you at any time
                </p>
              </div>
              
              <div style={styles.rightCard}>
                <div style={{ ...styles.rightIconWrapper, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                  <FileText size={28} color="white" />
                </div>
                <h4 style={styles.rightCardTitle}>Modify</h4>
                <p style={styles.rightCardDesc}>
                  Update or correct your personal information whenever needed
                </p>
              </div>
              
              <div style={styles.rightCard}>
                <div style={{ ...styles.rightIconWrapper, background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
                  <Database size={28} color="white" />
                </div>
                <h4 style={styles.rightCardTitle}>Export</h4>
                <p style={styles.rightCardDesc}>
                  Download your complete data in standard, portable formats
                </p>
              </div>
              
              <div style={styles.rightCard}>
                <div style={{ ...styles.rightIconWrapper, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <AlertTriangle size={28} color="white" />
                </div>
                <h4 style={styles.rightCardTitle}>Delete</h4>
                <p style={styles.rightCardDesc}>
                  Request permanent deletion of your data from our systems
                </p>
              </div>
              
              <div style={styles.rightCard}>
                <div style={{ ...styles.rightIconWrapper, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                  <Settings size={28} color="white" />
                </div>
                <h4 style={styles.rightCardTitle}>Restrict</h4>
                <p style={styles.rightCardDesc}>
                  Limit how your data is processed and used by our systems
                </p>
              </div>
              
              <div style={styles.rightCard}>
                <div style={{ ...styles.rightIconWrapper, background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)' }}>
                  <Lock size={28} color="white" />
                </div>
                <h4 style={styles.rightCardTitle}>Object</h4>
                <p style={styles.rightCardDesc}>
                  Opt out of specific data uses you don't consent to
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security for Providers & Organizations */}
      <section style={styles.providerSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Security for Providers & Organizations</h2>
          <p style={styles.sectionSubtitle}>
            Enterprise-grade protection for healthcare professionals and institutions
          </p>
          
          <div style={styles.providerGrid}>
            <div style={styles.providerCard}>
              <div style={styles.providerIconBg}>
                <Users size={32} color="#4facfe" />
              </div>
              <h3 style={styles.providerCardTitle}>Secure Dashboards</h3>
              <p style={styles.providerCardDesc}>
                Role-based access with isolated environments for each provider and organization
              </p>
            </div>
            
            <div style={styles.providerCard}>
              <div style={styles.providerIconBg}>
                <FileText size={32} color="#43e97b" />
              </div>
              <h3 style={styles.providerCardTitle}>Controlled Patient File Access</h3>
              <p style={styles.providerCardDesc}>
                Granular permissions ensure providers only access authorized patient records
              </p>
            </div>
            
            <div style={styles.providerCard}>
              <div style={styles.providerIconBg}>
                <Activity size={32} color="#fa709a" />
              </div>
              <h3 style={styles.providerCardTitle}>Encrypted Analytics</h3>
              <p style={styles.providerCardDesc}>
                Health insights and analytics protected with end-to-end encryption
              </p>
            </div>
            
            <div style={styles.providerCard}>
              <div style={styles.providerIconBg}>
                <Lock size={32} color="#667eea" />
              </div>
              <h3 style={styles.providerCardTitle}>Safe Storage of Clinical Notes</h3>
              <p style={styles.providerCardDesc}>
                All clinical documentation encrypted and access-logged for audit trails
              </p>
            </div>
            
            <div style={styles.providerCard}>
              <div style={styles.providerIconBg}>
                <Brain size={32} color="#30cfd0" />
              </div>
              <h3 style={styles.providerCardTitle}>Protected AI Risk Prediction</h3>
              <p style={styles.providerCardDesc}>
                AI-generated insights secured with strict multi-layer security protocols
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications & Compliance */}
      <section style={styles.certSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Certifications & Compliance Badges</h2>
          <p style={styles.sectionSubtitle}>
            Committed to meeting and exceeding global healthcare security standards
          </p>
          
          <div style={styles.certGrid}>
            {certifications.map((cert, index) => (
              <div key={index} style={styles.certCard}>
                <div style={styles.certBadgeWrapper}>
                  <Shield size={48} color={cert.statusColor} />
                </div>
                <h3 style={styles.certName}>{cert.name}</h3>
                <div style={{ ...styles.certStatus, background: `${cert.statusColor}20`, color: cert.statusColor }}>
                  {cert.status}
                </div>
                <p style={styles.certDesc}>{cert.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsible AI */}
      <section style={styles.aiSection}>
        <div style={styles.container}>
          <div style={styles.aiHeader}>
            <Brain size={56} color="#667eea" />
            <h2 style={styles.aiTitle}>Responsible AI at GEWO Health</h2>
          </div>
          
          <p style={styles.aiDescription}>
            Our AI systems, including the Health Genie, are built with safety, transparency, and 
            ethical considerations at their core
          </p>
          
          <div style={styles.aiGrid}>
            {aiPrinciples.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <div key={index} style={styles.aiCard}>
                  <div style={styles.aiIconWrapper}>
                    <Icon size={28} color="#667eea" />
                  </div>
                  <h3 style={styles.aiCardTitle}>{principle.title}</h3>
                  <p style={styles.aiCardDesc}>{principle.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={styles.faqSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Frequently Asked Questions</h2>
          <p style={styles.sectionSubtitle}>
            Common questions about data security and privacy
          </p>
          
          <div style={styles.faqList}>
            {faqs.map((faq, index) => (
              <div key={index} style={styles.faqItem}>
                <button 
                  style={styles.faqQuestion}
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <span style={styles.faqQuestionText}>{faq.question}</span>
                  {expandedFaq === index ? 
                    <ChevronUp size={24} color="#667eea" /> : 
                    <ChevronDown size={24} color="#667eea" />
                  }
                </button>
                {expandedFaq === index && (
                  <div style={styles.faqAnswer}>
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <div style={styles.ctaContent}>
            <div style={styles.ctaIconGroup}>
              <Shield size={72} color="white" />
            </div>
            
            <h2 style={styles.ctaTitle}>
              Security is our commitment. Your trust is our responsibility.
            </h2>
            
            <p style={styles.ctaSubtitle}>
              We're here to answer your security questions and address any concerns
            </p>
            
            <div style={styles.ctaButtons}>
              <button style={styles.ctaPrimaryButton}>
                <FileText size={20} style={{ marginRight: '10px' }} />
                View Privacy Policy
              </button>
              <button style={styles.ctaSecondaryButton}>
                <Users size={20} style={{ marginRight: '10px' }} />
                Contact Security Team
              </button>
              <button style={styles.ctaOutlineButton}>
                <AlertTriangle size={20} style={{ marginRight: '10px' }} />
                Report a Security Concern
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FFFFFF'
  },
  
  // Hero Section
  heroSection: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    paddingTop: '100px',
    paddingBottom: '80px',
    position: 'relative',
    overflow: 'hidden'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px'
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px',
    alignItems: 'center'
  },
  heroContent: {
    color: 'white'
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255, 255, 255, 0.2)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '24px',
    backdropFilter: 'blur(10px)'
  },
  heroTitle: {
    fontSize: '52px',
    fontWeight: '800',
    lineHeight: '1.2',
    marginBottom: '20px'
  },
  heroSubtitle: {
    fontSize: '18px',
    lineHeight: '1.7',
    marginBottom: '32px',
    opacity: 0.95
  },
  heroButtons: {
    display: 'flex',
    gap: '16px',
    marginBottom: '40px',
    flexWrap: 'wrap'
  },
  heroPrimaryButton: {
    background: 'white',
    color: '#667eea',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.2s ease'
  },
  heroSecondaryButton: {
    background: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    border: '2px solid white',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    backdropFilter: 'blur(10px)',
    transition: 'transform 0.2s ease'
  },
  trustIndicators: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap'
  },
  trustItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    background: 'rgba(255, 255, 255, 0.15)',
    padding: '8px 16px',
    borderRadius: '20px',
    backdropFilter: 'blur(10px)'
  },
  heroIllustration: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  
  // Introduction Section
  introSection: {
    padding: '80px 0',
    background: '#f7f9fc'
  },
  introContent: {
    maxWidth: '900px',
    margin: '0 auto'
  },
  sectionTitle: {
    fontSize: '42px',
    fontWeight: '800',
    color: '#1a202c',
    textAlign: 'center',
    marginBottom: '16px'
  },
  introParagraph: {
    fontSize: '17px',
    color: '#4a5568',
    lineHeight: '1.8',
    marginBottom: '20px',
    textAlign: 'center'
  },
  missionHighlight: {
    background: 'linear-gradient(135deg, #e3edff 0%, #f7f9fc 100%)',
    borderRadius: '20px',
    padding: '40px',
    marginTop: '40px',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    border: '2px solid #667eea20'
  },
  missionIcon: {
    width: '80px',
    height: '80px',
    background: 'white',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)'
  },
  missionTitle: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#667eea',
    marginBottom: '8px'
  },
  missionDesc: {
    fontSize: '17px',
    color: '#4a5568',
    lineHeight: '1.6',
    margin: 0
  },
  
  // Core Security Pillars
  pillarsSection: {
    padding: '80px 0',
    background: '#ffffff'
  },
  sectionSubtitle: {
    fontSize: '18px',
    color: '#718096',
    textAlign: 'center',
    marginBottom: '60px'
  },
  pillarsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '32px'
  },
  pillarCard: {
    background: '#ffffff',
    borderRadius: '24px',
    padding: '32px',
    border: '2px solid #e2e8f0',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  },
  pillarIconWrapper: {
    width: '72px',
    height: '72px',
    borderRadius: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
  },
  pillarTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '12px'
  },
  pillarDescription: {
    fontSize: '15px',
    color: '#718096',
    lineHeight: '1.6',
    marginBottom: '20px'
  },
  pillarDetailsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  pillarDetailItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px'
  },
  checkIcon: {
    marginTop: '2px',
    flexShrink: 0
  },
  pillarDetailText: {
    fontSize: '14px',
    color: '#4a5568',
    lineHeight: '1.5'
  },
  
  // Protection Section
  protectionSection: {
    padding: '80px 0',
    background: '#f7f9fc'
  },
  protectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px'
  },
  protectionCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px 24px',
    textAlign: 'center',
    border: '2px solid #e2e8f0',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  },
  protectionIconWrapper: {
    width: '80px',
    height: '80px',
    margin: '0 auto 20px',
    background: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '3px solid',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
  },
  protectionEmoji: {
    fontSize: '40px'
  },
  protectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '8px'
  },
  protectionDesc: {
    fontSize: '14px',
    color: '#718096',
    lineHeight: '1.5'
  },
  
  // Data Section
  dataSection: {
    padding: '80px 0',
    background: '#ffffff'
  },
  dataGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center'
  },
  dataIllustration: {
    display: 'flex',
    justifyContent: 'center'
  },
  dataContent: {},
  dataTitle: {
    fontSize: '38px',
    fontWeight: '800',
    color: '#1a202c',
    marginBottom: '16px'
  },
  dataDescription: {
    fontSize: '17px',
    color: '#718096',
    lineHeight: '1.7',
    marginBottom: '32px'
  },
  dataPoints: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  dataPoint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px'
  },
  dataPointIcon: {
    width: '48px',
    height: '48px',
    background: '#f7f9fc',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  dataPointTitle: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '4px'
  },
  dataPointDesc: {
    fontSize: '15px',
    color: '#718096',
    lineHeight: '1.6',
    margin: 0
  },
  
  // Incident Section
  incidentSection: {
    padding: '80px 0',
    background: '#f7f9fc'
  },
  incidentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px'
  },
  incidentCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px 24px',
    textAlign: 'center',
    border: '2px solid #e2e8f0'
  },
  incidentIconWrapper: {
    marginBottom: '20px'
  },
  incidentTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '12px'
  },
  incidentDesc: {
    fontSize: '14px',
    color: '#718096',
    lineHeight: '1.6'
  },
  
  // Privacy Section
  privacySection: {
    padding: '80px 0',
    background: '#ffffff'
  },
  privacyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px',
    marginBottom: '60px'
  },
  privacyCard: {
    background: '#f7f9fc',
    borderRadius: '20px',
    padding: '32px 24px',
    textAlign: 'center',
    border: '2px solid #e2e8f0'
  },
  privacyIconCircle: {
    width: '64px',
    height: '64px',
    background: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
  },
  privacyCardTitle: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '10px'
  },
  privacyCardDesc: {
    fontSize: '14px',
    color: '#718096',
    lineHeight: '1.6'
  },
  rightsSection: {
    marginTop: '60px',
    padding: '60px',
    background: 'linear-gradient(135deg, #f7f9fc 0%, #e3edff 100%)',
    borderRadius: '32px',
    border: '2px solid #e2e8f0'
  },
  rightsSectionTitle: {
    fontSize: '32px',
    fontWeight: '800',
    color: '#1a202c',
    textAlign: 'center',
    marginBottom: '12px'
  },
  rightsSectionSubtitle: {
    fontSize: '17px',
    color: '#718096',
    textAlign: 'center',
    marginBottom: '48px'
  },
  rightsCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px'
  },
  rightCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px 24px',
    textAlign: 'center',
    border: '2px solid #e2e8f0',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.02)'
  },
  rightIconWrapper: {
    width: '72px',
    height: '72px',
    borderRadius: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
  },
  rightCardTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '12px'
  },
  rightCardDesc: {
    fontSize: '14px',
    color: '#718096',
    lineHeight: '1.6',
    margin: 0
  },
  
  // Provider Section
  providerSection: {
    padding: '80px 0',
    background: '#f7f9fc'
  },
  providerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px'
  },
  providerCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px',
    border: '2px solid #e2e8f0',
    transition: 'all 0.3s ease'
  },
  providerIconBg: {
    width: '64px',
    height: '64px',
    background: '#f7f9fc',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px'
  },
  providerCardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '10px'
  },
  providerCardDesc: {
    fontSize: '14px',
    color: '#718096',
    lineHeight: '1.6'
  },
  
  // Certifications Section
  certSection: {
    padding: '80px 0',
    background: '#ffffff'
  },
  certGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px'
  },
  certCard: {
    background: '#f7f9fc',
    borderRadius: '20px',
    padding: '40px 24px',
    textAlign: 'center',
    border: '2px solid #e2e8f0'
  },
  certBadgeWrapper: {
    marginBottom: '20px'
  },
  certName: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '12px'
  },
  certStatus: {
    display: 'inline-block',
    padding: '6px 16px',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '700',
    marginBottom: '12px'
  },
  certDesc: {
    fontSize: '14px',
    color: '#718096'
  },
  
  // AI Section
  aiSection: {
    padding: '80px 0',
    background: 'linear-gradient(180deg, #f7f9fc 0%, #e3edff 100%)'
  },
  aiHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '24px'
  },
  aiTitle: {
    fontSize: '38px',
    fontWeight: '800',
    color: '#1a202c'
  },
  aiDescription: {
    fontSize: '17px',
    color: '#4a5568',
    lineHeight: '1.8',
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto 48px'
  },
  aiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '20px'
  },
  aiCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center',
    border: '2px solid #e2e8f0'
  },
  aiIconWrapper: {
    width: '56px',
    height: '56px',
    background: '#e3edff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  },
  aiCardTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '8px'
  },
  aiCardDesc: {
    fontSize: '13px',
    color: '#718096',
    lineHeight: '1.5'
  },
  
  // FAQ Section
  faqSection: {
    padding: '80px 0',
    background: '#ffffff'
  },
  faqList: {
    maxWidth: '900px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  faqItem: {
    background: '#f7f9fc',
    borderRadius: '16px',
    border: '2px solid #e2e8f0',
    overflow: 'hidden'
  },
  faqQuestion: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 28px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left'
  },
  faqQuestionText: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#1a202c'
  },
  faqAnswer: {
    padding: '0 28px 24px',
    fontSize: '15px',
    color: '#4a5568',
    lineHeight: '1.7'
  },
  
  // CTA Section
  ctaSection: {
    padding: '80px 0',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white'
  },
  ctaContent: {
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto'
  },
  ctaIconGroup: {
    marginBottom: '24px'
  },
  ctaTitle: {
    fontSize: '40px',
    fontWeight: '800',
    marginBottom: '20px'
  },
  ctaSubtitle: {
    fontSize: '18px',
    marginBottom: '40px',
    opacity: 0.95
  },
  ctaButtons: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  ctaPrimaryButton: {
    background: 'white',
    color: '#667eea',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.2s ease'
  },
  ctaSecondaryButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    backdropFilter: 'blur(10px)',
    transition: 'transform 0.2s ease'
  },
  ctaOutlineButton: {
    background: 'transparent',
    color: 'white',
    border: '2px solid white',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'transform 0.2s ease'
  }
};

export default DataSecurity;
